import React, { FC, Suspense, useEffect, useMemo } from "react";
import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
  matchPath,
} from "react-router-dom";
import { History } from "history";
import { useTranslation } from "react-i18next";
import stepsRoutes from "./steps/routes";
import pagesRoutes from "./pages/routes";
import Layout from "./layout";
import { setCurrentLocale } from "./store/shared/locales";
import { mainRoute } from "./steps/Main/route";
import { SettingsEndpoint } from "./store/endpoints";
import "./store/selected/dependencies";
import "./store/dependencies";
import { AppConfig } from "./config";
import { Progress } from "./components";
import { useStore } from "effector-react";
import { $countdown } from "./store/shared/countdown";
import Timeout from "./blockers/Timeout";
import SpamDetected from "./blockers/SpamDetected";
import {
  paymentStatusRoute,
  RESERVATION_UUID_QUERY_PARAM_NAME,
} from "./pages/PaymentStatus/route";
import { hexToRgb, isPresent, qs, setMetaElement } from "./utils";
import { setOnBeforeUnload, resetOnBeforeUnload } from "./shared/beforeUnload";
import { $spamDetected } from "./store/shared/spamDetected";
import { addCustomScripts } from "./utils/addCustomScripts";

// TODO: watch https://github.com/ReactTraining/history/issues/810 and use Router with basename & history

export let history: History | null = null;

// to be always full screen (even on mobile) https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
// const handleVh = (): void => {
//   const vh = window.innerHeight * 0.01;
//   document.body.style.setProperty("--vh", `${vh}px`);
// };
// handleVh();
// window.addEventListener("resize", handleVh);

const App: FC = () => {
  const localHistory = useHistory();
  const { data: settings } = useStore(SettingsEndpoint.store);
  const { expired: sessionExpired } = useStore($countdown);
  const spamDetected = useStore($spamDetected);
  const { search: searchParams, pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    SettingsEndpoint.call({
      _query: { lang: i18n.language },
    }).then((settings) => {
      document
        .getElementById("favicon")
        ?.setAttribute("href", settings.logo ?? AppConfig.defaultFaviconUrl);
    });
    return () => {
      SettingsEndpoint.cancel();
    };
  }, [i18n.language]);

  useEffect(() => {
    setCurrentLocale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    history = localHistory;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const res = qs.parse(searchParams.toString().substr(1));
    if (res?.reservation) {
      const query = qs.stringify({
        [RESERVATION_UUID_QUERY_PARAM_NAME]: res.reservation,
      });
      localHistory.push(`${paymentStatusRoute.path}${query}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const isStepRouteActive = useMemo(() => {
    return stepsRoutes.some((route) => matchPath(pathname, route));
  }, [pathname]);

  useEffect(() => {
    setOnBeforeUnload();
    return (): void => {
      resetOnBeforeUnload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (settings) {
      Object.entries(settings.seo).forEach(([name, content]) => {
        setMetaElement(name, content);
      });

      if (settings.primary_color) {
        setMetaElement("theme-color", settings.primary_color);
        document.body.style.setProperty("--c-primary", settings.primary_color);
        document.body.style.setProperty(
          "--c-primary_rgb",
          hexToRgb(settings.primary_color)?.join(", ") ?? null
        );
      }

      if (settings.custom_js_code && DOMParser && !AppConfig.isDevelopment) {
        const promises = addCustomScripts(settings.custom_js_code);

        Promise.all(promises).then(() => {
          if (isPresent(window.ga)) {
            localHistory.listen((location: any) => {
              if (isPresent(window.ga)) {
                window.ga("set", "page", location.pathname + location.search);
                window.ga("send", "pageview");
              }
            });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  return (
    <Layout
      hideFooter={sessionExpired || spamDetected || !isStepRouteActive}
      hideAside={sessionExpired || spamDetected || !isStepRouteActive}
    >
      {sessionExpired && <Timeout />}
      {spamDetected && <SpamDetected />}

      <Suspense fallback={<Progress fill />}>
        <Switch>
          {!sessionExpired &&
            !spamDetected &&
            stepsRoutes.map((route) => (
              <Route key={route.path + ""} {...route} />
            ))}

          {pagesRoutes.map((route) => (
            <Route key={route.path + ""} {...route} />
          ))}
          <Redirect to={mainRoute.path} />
        </Switch>
      </Suspense>
    </Layout>
  );
};

export default App;
